import type { RoleDefinition, ScoringWeights } from '../types/api';

export const DEFAULT_WEIGHTS: ScoringWeights = {
  qualifications: 4,
  outcomes: 3,
  experience: 3,
  certifications: 2,
  education: 2,
  companyFit: 3,
};

export interface RolePreset {
  id: string;
  label: string;
  shortLabel: string;
  description: string;
  role: RoleDefinition;
}

export const ROLE_PRESETS: RolePreset[] = [
  {
    id: 'transformation',
    label: 'VP, Workforce Transformation',
    shortLabel: 'Transformation',
    description: 'Classic transformation role: needs both operational scale and innovation DNA. Favors candidates who build new systems, not just optimize existing ones.',
    role: {
      title: 'VP, Workforce Transformation',
      qualifications: [
        'Contact center operations leadership at scale (50+ centers)',
        'WFM strategy and technology modernization',
        'P&L ownership with $20M+ budget responsibility',
        'AI/automation integration for workforce operations',
        'Track record of measurable cost reduction and quality improvement',
      ],
      outcomes: [
        'Reduce operational costs by 20% within 18 months',
        'Modernize WFM technology stack for AI readiness',
        'Build data-driven workforce planning capability',
        'Improve employee experience scores by 15%',
      ],
      companyType: 'Fortune 500',
      yearsExperience: '15+',
      certifications: 'Six Sigma, PMP preferred',
      jobClassification: 'hybrid' as const,
      education: "Bachelor's required, MBA preferred",
      weights: { qualifications: 4, outcomes: 3, experience: 3, certifications: 2, education: 2, companyFit: 3 },
    },
  },
  {
    id: 'operations',
    label: 'SVP, Contact Center Operations',
    shortLabel: 'Operations',
    description: 'Stability-focused: needs someone to run a large, well-oiled machine. Favors certifications, vendor expertise, and consistent SLA track records over innovation.',
    role: {
      title: 'SVP, Contact Center Operations',
      qualifications: [
        'Multi-site contact center management (5,000+ agents)',
        'COPC certification and Six Sigma methodology',
        'Vendor platform expertise (Verint, NICE, Genesys)',
        'SLA management with proven consistency track record',
        'Budget management ($40M+) and cost optimization',
      ],
      outcomes: [
        'Maintain 95%+ SLA attainment across all centers',
        'Reduce cost per contact through operational efficiency',
        'Standardize processes across all sites using proven frameworks',
        'Consolidate vendor landscape for cost savings',
      ],
      companyType: 'Fortune 500',
      yearsExperience: '15+',
      certifications: 'COPC, Six Sigma Black Belt, PMP required',
      jobClassification: 'office' as const,
      education: "Bachelor's required",
      weights: { qualifications: 4, outcomes: 3, experience: 4, certifications: 5, education: 2, companyFit: 3 },
    },
  },
  {
    id: 'intelligent-ops',
    label: 'VP, Intelligent Operations & Analytics',
    shortLabel: 'Intelligent Ops',
    description: 'Innovation-focused: needs someone who builds ML systems, not configures vendor tools. Favors technical depth, published research, and cross-industry transformation.',
    role: {
      title: 'VP, Intelligent Operations & Analytics',
      qualifications: [
        'Machine learning and predictive analytics for operations',
        'Custom system development (not just vendor implementation)',
        'Cross-industry transformation experience',
        'Published research or patents in operations/workforce optimization',
        'Operations research or data science background',
      ],
      outcomes: [
        'Build ML-powered demand forecasting reducing overstaffing by 20%+',
        'Replace reactive management with predictive intelligence systems',
        'Improve NPS/customer satisfaction through data-driven redesign',
        'Create real-time decisioning engine for workforce allocation',
      ],
      companyType: 'Growth-stage',
      yearsExperience: '10+',
      certifications: 'None required — demonstrated innovation preferred over credentials',
      jobClassification: 'hybrid' as const,
      education: "Master's in Operations Research, Data Science, or related field",
      weights: { qualifications: 5, outcomes: 4, experience: 2, certifications: 1, education: 4, companyFit: 3 },
    },
  },
  {
    id: 'people-experience',
    label: 'Chief People Experience Officer',
    shortLabel: 'People Experience',
    description: 'Counterintuitive: sounds like an HR role, but the outcomes require predictive modeling, real-time systems, and behavioral analytics. Watch how the ATS and Intelligence Engine interpret this differently.',
    role: {
      title: 'Chief People Experience Officer',
      qualifications: [
        'Behavioral analytics and employee journey mapping',
        'Real-time workforce sentiment monitoring systems',
        'Predictive attrition modeling and intervention design',
        'Cross-functional transformation (not just HR)',
        'AI/ML tools for workforce experience optimization',
      ],
      outcomes: [
        'Reduce agent attrition by 30% using predictive models',
        'Build real-time employee experience dashboard',
        'Design intelligent scheduling that balances business needs with agent preferences',
        'Create feedback loop between workforce data and operational decisions',
      ],
      companyType: 'Technology Vendor',
      yearsExperience: '10+',
      certifications: 'None',
      jobClassification: 'remote' as const,
      education: 'Advanced degree preferred',
      weights: { qualifications: 5, outcomes: 5, experience: 2, certifications: 1, education: 3, companyFit: 4 },
    },
  },
  {
    id: 'legacy-modernization',
    label: 'VP, Legacy Systems Modernization',
    shortLabel: 'Legacy Modernization',
    description: 'Counterintuitive: "legacy" sounds like it needs a maintainer, but the requirements describe replacing old systems with intelligent automation. ATS keyword matching fails here — the best candidates don\'t have "legacy" on their resume.',
    role: {
      title: 'VP, Legacy Systems Modernization',
      qualifications: [
        'Track record replacing manual/legacy processes with intelligent automation',
        'Experience migrating from vendor-dependent to custom-built systems',
        'Change management in risk-averse organizations',
        'AI/ML infrastructure development',
        'Ability to build business cases for technology transformation',
      ],
      outcomes: [
        'Replace Erlang-based forecasting with ML-powered predictions',
        'Migrate from vendor-locked WFM platform to open, API-driven architecture',
        'Reduce technology licensing costs by 40% through custom builds',
        'Build internal data science capability where none exists',
      ],
      companyType: 'Fortune 500',
      yearsExperience: '12+',
      certifications: 'Not valued — builder mindset over credential collection',
      jobClassification: 'hybrid' as const,
      education: 'Technical degree preferred (CS, OR, Engineering)',
      weights: { qualifications: 5, outcomes: 5, experience: 3, certifications: 1, education: 3, companyFit: 4 },
    },
  },
];

export const DEFAULT_ROLE: RoleDefinition = ROLE_PRESETS[0].role;
